<template>
  <modal
    name="showInfo"
    style="z-index: 100000001"
    :height="'auto'"
    :width="430"
    :adaptive="true"
    classes="annotation-vue-modal"
    @before-close="resetDialog"
  >
    <div class="annotation-dialog">

      <!-- INFORMACIÓN Y EDICIÓN -->
      <template v-if="!deleteWarning">
        <div class="annotation-dialog-header">
          <h2>
            {{ editMode ? 'Editar anotación' : 'Detalle de anotación' }}
          </h2>
        </div>

        <div class="annotation-dialog-body">

          <!-- Autor -->
          <div
            v-if="!isOwnAnnotation"
            class="annotation-detail-group"
          >
            <span class="annotation-detail-label">
              Creada por
            </span>

            <p class="annotation-detail-value">
              {{ authorName }}
            </p>
          </div>

          <!-- Visualización -->
          <template v-if="!editMode">
            <div class="annotation-detail-group">
              <span class="annotation-detail-label">
                Texto
              </span>

              <p class="annotation-detail-text">
                {{ info.text }}
              </p>
            </div>

            <AnnotationStyleEditor
              :value="normalizedInfoStyle"
              :is-readonly="true"
            />

            <div
              v-if="isOwnAnnotation"
              class="annotation-shared-status"
            >
              <span>Anotación compartida</span>

              <strong>
                {{ info.shared ? 'Sí' : 'No' }}
              </strong>
            </div>

            <div
              v-if="info.shared && selectedGroupNames.length"
              class="annotation-detail-group"
            >
              <span class="annotation-detail-label">
                Grupos
              </span>

              <p class="annotation-detail-value">
                {{ selectedGroupNames.join(', ') }}
              </p>
            </div>
          </template>

          <!-- Edición -->
          <template v-else>
            <div class="annotation-form-group">
              <label for="edit-annotation-text">
                Texto
              </label>

              <textarea
                id="edit-annotation-text"
                v-model="newInfo.text"
                maxlength="100"
              ></textarea>

              <span class="annotation-character-count">
                {{ newInfoTextLength }} / 100
              </span>
            </div>

            <AnnotationStyleEditor v-model="newInfo.style" />

            <div class="annotation-form-group">
              <label for="edit-annotation-shared">
                Anotación compartida
              </label>

              <select
                id="edit-annotation-shared"
                v-model="newInfo.shared"
              >
                <option :value="true">
                  Sí
                </option>

                <option :value="false">
                  No
                </option>
              </select>
            </div>

            <AnnotationGroupSelector
              v-if="newInfo.shared"
              v-model="newInfo.groupIds"
              :groups="groups"
            />

            <button
              type="button"
              class="annotation-delete-btn"
              @click="deleteWarning = true"
            >
              <font-awesome-icon icon="trash-alt" />
              Eliminar anotación
            </button>
          </template>
        </div>

        <!-- Acciones modo visualización -->
        <div
          v-if="!editMode"
          class="annotation-dialog-actions"
        >
          <button
            type="button"
            class="annotation-dialog-btn annotation-dialog-btn--secondary"
            @click="$modal.hide('showInfo')"
          >
            Cerrar
          </button>

          <!-- La anotación propia se elimina desde el modo edición, como
               siempre; la ajena no tiene ese modo, así que administración de
               usuarios necesita su propia entrada acá. -->
          <button
            v-if="!isOwnAnnotation && canDelete"
            type="button"
            class="annotation-dialog-btn annotation-dialog-btn--danger"
            @click="deleteWarning = true"
          >
            Eliminar
          </button>

          <button
            v-if="isOwnAnnotation"
            type="button"
            class="annotation-dialog-btn annotation-dialog-btn--primary"
            @click="editAnnotation()"
          >
            Editar
          </button>
        </div>

        <!-- Acciones modo edición -->
        <div
          v-else
          class="annotation-dialog-actions"
        >
          <button
            type="button"
            class="annotation-dialog-btn annotation-dialog-btn--secondary"
            @click="editMode = false"
          >
            Cancelar
          </button>

          <button
            v-if="isOwnAnnotation"
            type="button"
            class="annotation-dialog-btn annotation-dialog-btn--primary"
            :disabled="isSaving"
            @click="putAnnotation()"
          >
            {{ isSaving ? 'Guardando…' : 'Guardar' }}
          </button>
        </div>
      </template>

      <!-- CONFIRMACIÓN DE ELIMINACIÓN -->
      <template v-else>
        <div class="annotation-dialog-header">
          <h2>Eliminar anotación</h2>
        </div>

        <div class="annotation-dialog-body">
          <div class="annotation-delete-warning">
            <strong>
              ¿Está seguro de que desea eliminar esta anotación?
            </strong>

            <p>
              Esta acción no se puede deshacer.
            </p>
          </div>
        </div>

        <div class="annotation-dialog-actions">
          <button
            type="button"
            class="annotation-dialog-btn annotation-dialog-btn--secondary"
            @click="deleteWarning = false"
          >
            No, cancelar
          </button>

          <button
            v-if="canDelete"
            type="button"
            class="annotation-dialog-btn annotation-dialog-btn--danger"
            @click="deleteAnnotation()"
          >
            Sí, eliminar
          </button>
        </div>
      </template>

    </div>
  </modal>
</template>

<script>
import AnnotationStyleEditor from './AnnotationStyleEditor.vue'
import AnnotationGroupSelector from './AnnotationGroupSelector.vue'
import {
  cloneAnnotationStyle,
  DEFAULT_USER_ANNOTATION_STYLE,
  normalizeAnnotationStyle
} from './annotationStyles.js'

export default {
    name: 'AnnotationInfo',
    props: {
        info: {
            type: [Object, String],
            default: ''
        },
        groups: {
            type: Array,
            default: function () {
                return []
            }
        }
    },
    components: {
        AnnotationStyleEditor,
        AnnotationGroupSelector
    },
    data(){
        return{
            editMode: false,
            newInfo:{
                text:"",
                shared: false,
                groupIds: [],
                style: cloneAnnotationStyle(DEFAULT_USER_ANNOTATION_STYLE)
            },
            deleteWarning: false,
            isSaving: false
        }
    },
    computed: {
        newInfoTextLength: function () {
            return Array.from(this.newInfo.text || '').length
        },
        normalizedInfoStyle: function () {
            return normalizeAnnotationStyle(
                this.info && this.info.style,
                DEFAULT_USER_ANNOTATION_STYLE
            )
        },
        isOwnAnnotation: function () {
            return !this.info || !this.info.user
        },
        // El nombre lo trae la propia anotación (GET /annotations/:id/group):
        // pedirlo a /users/:id exigía permiso de administración de usuarios.
        authorName: function () {
            return (this.info && this.info.author) || 'Sin nombre'
        },
        // Solo quien administra usuarios puede eliminar anotaciones ajenas.
        canDelete: function () {
            const permissions = this.$store.getters.getPermissions || {}
            return this.isOwnAnnotation || !!permissions.users
        },
        selectedGroupNames: function () {
            const selectedIds = this.info && this.info.groups ? this.info.groups : []
            return this.groups
                .filter(group => selectedIds.indexOf(group.id) !== -1)
                .map(group => group.name)
        }
    },
    methods: {
        putAnnotation: function(){
            var newData = {
                "id_map": this.info.map,
                "id_groups": this.newInfo.shared ? this.newInfo.groupIds : [],
                "text": this.newInfo.text.trim(),
                "location": {
                    "type": "Point",
                    "coordinates": this.info.coordinates
                },
                "is_shared": this.newInfo.shared,
                "style": cloneAnnotationStyle(this.newInfo.style)
            }

            var that=this
            if (!newData.text) {
                this.$store.dispatch('toast/show', {
                    message: 'Escribe un texto para la anotación.',
                    type: 'warning'
                })
                return
            }
            if (newData.is_shared && !newData.id_groups.length) {
                this.$store.dispatch('toast/show', {
                    message: 'Selecciona al menos un grupo para compartir la anotación.',
                    type: 'warning'
                })
                return
            }
            this.isSaving = true
            this.$http.put(this.$store.getters.backendurl + 'api/v1/annotations/' + this.info.id, newData)
            .then(function(response){
              that.editMode=false
              that.$modal.hide('showInfo')
              that.$emit('saveEdit')
              that.$store.dispatch('toast/show', {
                message: 'Anotación actualizada.',
                type: 'success'
              })
            })
            .catch(function (error) {
                console.error(error)
                that.$store.dispatch('toast/show', {
                    message: 'No fue posible actualizar la anotación.',
                    type: 'error'
                })
            })
            .then(function () {
                that.isSaving = false
            });
        },
        deleteAnnotation: function(){
            console.log("eliminar anotacion")
            var that=this
            this.$http.delete(this.$store.getters.backendurl + 'api/v1/annotations/' + this.info.id)
            .then(function(response){
              that.editMode=that.$modal.hide("showInfo")
              that.deleteWarning=false
              that.$emit('saveEdit')
            })
            .catch(function (error) {
                console.error(error)
                that.$store.dispatch('toast/show', {
                    message: 'No fue posible eliminar la anotación.',
                    type: 'error'
                })
            });
        },
        editAnnotation: function(){
            this.newInfo.text=this.info.text
            this.newInfo.shared=this.info.shared
            this.newInfo.groupIds=(this.info.groups || [])
                .filter(groupId => this.groups.some(group => group.id === groupId))
            this.newInfo.style=cloneAnnotationStyle(this.normalizedInfoStyle)
            this.editMode=true
        },
        resetDialog: function () {
            this.editMode = false
            this.deleteWarning = false
            this.isSaving = false
        }
    },
    watch:{
        'newInfo.shared': function (value) {
            if (!value) this.newInfo.groupIds = []
        },
        info: function(value){
            if(value){
                this.$modal.show("showInfo")
            }
        }
    }
}
</script>
